import type maplibregl from "maplibre-gl";

export type BasemapId =
  | "carto"        // CARTO (theme-aware dark/light) — default
  | "osm"          // OpenStreetMap standard
  | "voyager"      // CARTO Voyager (Google-Maps style)
  | "positron"     // CARTO Positron (always light)
  | "dark-matter"  // CARTO Dark Matter (always dark)
  | "topo"         // OpenTopoMap (contours + hillshade)
  | "hot"          // HOT Humanitarian (for emergency planning)
  | "esri-street"  // Esri World Street Map
  | "esri-topo"    // Esri World Topo Map
  | "satellite"    // Esri satellite (no labels)
  | "hybrid";      // Esri satellite + street labels

export interface BasemapOption {
  id: BasemapId;
  label: string;
  preview: string; // description / subtitle
  hasLabels: boolean;
}

export const BASEMAP_OPTIONS: BasemapOption[] = [
  { id: "carto",       label: "CARTO",      preview: "Theme-aware dark/light", hasLabels: true  },
  { id: "osm",         label: "OSM",        preview: "OpenStreetMap standard", hasLabels: true  },
  { id: "voyager",     label: "Voyager",    preview: "CARTO colorful streets", hasLabels: true  },
  { id: "positron",    label: "Positron",   preview: "CARTO light — always", hasLabels: true  },
  { id: "dark-matter", label: "Dark",       preview: "CARTO dark — always", hasLabels: true  },
  { id: "topo",        label: "Topo",       preview: "Contours + hillshade", hasLabels: true  },
  { id: "hot",         label: "HOT",        preview: "Humanitarian mapping", hasLabels: true  },
  { id: "esri-street", label: "Street",     preview: "Esri World Street Map", hasLabels: true  },
  { id: "esri-topo",   label: "Esri Topo",  preview: "Esri World Topo", hasLabels: true  },
  { id: "satellite",   label: "Satellite",  preview: "Esri aerial, no labels", hasLabels: false },
  { id: "hybrid",      label: "Hybrid",     preview: "Satellite + road labels", hasLabels: true  },
];

function rasterStyle(
  tiles: string[],
  attribution = "© OpenStreetMap contributors",
  maxzoom = 19,
): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      base: {
        type: "raster",
        tiles,
        tileSize: 256,
        attribution,
        maxzoom,
      },
    },
    layers: [{ id: "basemap", type: "raster", source: "base" }],
  };
}

// CARTO free raster tiles retired 2026-08 (every _nolabels/_only_labels tile
// now watermarked "API KEY REQUIRED") — swapped to keyless Esri Canvas Gray
// on arcgisonline. Its metadata advertises LODs through z23, but this
// region's tile cache stops at z16: past that it silently returns a 2.5KB
// "Map data not yet available" placeholder (HTTP 200) — capping maxzoom
// there makes MapLibre overzoom instead of requesting it. `slug`/`labelsSlug`
// are now Esri Canvas service names ("World_Dark_Gray_Base" etc), not CARTO
// style tokens — the params are kept so the four call sites below don't
// need to change shape.
function cartoStyle(slug: string, labelsSlug: string, oceanBg: string, baseOpacity: number, labelOpacity: number): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      "carto-base": {
        type: "raster",
        tiles: [`https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${slug}/MapServer/tile/{z}/{y}/{x}`],
        tileSize: 256,
        attribution: "Esri, HERE, Garmin · © OpenStreetMap",
        maxzoom: 16,
      },
      "carto-labels": {
        type: "raster",
        tiles: [`https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${labelsSlug}/MapServer/tile/{z}/{y}/{x}`],
        tileSize: 256,
        maxzoom: 16,
      },
    },
    layers: [
      { id: "ocean-bg", type: "background", paint: { "background-color": oceanBg } },
      { id: "basemap",  type: "raster", source: "carto-base",   paint: { "raster-opacity": baseOpacity } },
      { id: "labels-top", type: "raster", source: "carto-labels", paint: { "raster-opacity": labelOpacity } },
    ],
  };
}

function esriStyle(serviceSlug: string, labelsSlug?: string): maplibregl.StyleSpecification {
  const base = `https://services.arcgisonline.com/arcgis/rest/services/${serviceSlug}/MapServer/tile/{z}/{y}/{x}`;
  const sources: maplibregl.StyleSpecification["sources"] = {
    "esri-base": { type: "raster", tiles: [base], tileSize: 256, maxzoom: 19, attribution: "© Esri" },
  };
  const layers: maplibregl.StyleSpecification["layers"] = [
    { id: "basemap", type: "raster", source: "esri-base" },
  ];
  if (labelsSlug) {
    const labels = `https://services.arcgisonline.com/arcgis/rest/services/${labelsSlug}/MapServer/tile/{z}/{y}/{x}`;
    sources["esri-labels"] = { type: "raster", tiles: [labels], tileSize: 256, maxzoom: 19 };
    layers.push({ id: "labels-top", type: "raster", source: "esri-labels" });
  }
  return { version: 8, sources, layers };
}

export function buildBasemapStyle(
  id: BasemapId,
  theme: "dark" | "light" | "a11y",
): maplibregl.StyleSpecification {
  // The a11y theme is a dark ground (pure black), so it borrows the
  // dark basemap. Only the tile set differs from light — the panel
  // chrome around it is handled by tokens.css.
  const isDark = theme !== "light";
  switch (id) {
    case "carto":
      return isDark
        ? cartoStyle("World_Dark_Gray_Base",  "World_Dark_Gray_Reference",  "#031730", 0.78, 0.85)
        : cartoStyle("World_Light_Gray_Base", "World_Light_Gray_Reference", "#E0F2FE", 0.92, 0.90);

    case "positron":
      return cartoStyle("World_Light_Gray_Base", "World_Light_Gray_Reference", "#E0F2FE", 0.95, 0.90);

    case "dark-matter":
      return cartoStyle("World_Dark_Gray_Base", "World_Dark_Gray_Reference", "#031730", 0.90, 0.88);

    case "voyager":
      return rasterStyle(
        ["https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"],
        "© OpenStreetMap, © CARTO",
      );

    case "osm":
      return rasterStyle(
        ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
        "© OpenStreetMap contributors",
      );

    case "hot":
      return rasterStyle(
        [
          "https://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
          "https://b.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
        ],
        "© OpenStreetMap contributors, Tiles courtesy of HOT",
      );

    case "topo":
      return rasterStyle(
        ["https://tile.opentopomap.org/{z}/{x}/{y}.png"],
        "© OpenStreetMap contributors, © OpenTopoMap (CC-BY-SA)",
        17,
      );

    case "esri-street":
      return esriStyle("World_Street_Map");

    case "esri-topo":
      return esriStyle("World_Topo_Map");

    case "satellite":
      return esriStyle("World_Imagery");

    case "hybrid":
      return esriStyle("World_Imagery", "Reference/World_Transportation");
  }
}
